import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToastrService } from 'ngx-toastr';
import type { MitTransactionDTO, ResolveMitTransactionRequest } from '../../core/models';
import { MitService, mitApiError } from '../../services/mit.service';

type Outcome = ResolveMitTransactionRequest['outcome'];

/**
 * Resolución de plataforma: un cargo en revisión se marca aprobado o declinado; la cancelación pendiente o fallida
 * de un cargo de validación se confirma cuando se hizo fuera de Pento.
 */
@Component({
  selector: 'app-mit-transaction-resolve-dialog',
  templateUrl: './mit-transaction-resolve-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ReactiveFormsModule, DialogModule, ButtonModule, FloatLabelModule, InputTextModule, TextareaModule, SelectButtonModule]
})
export class MitTransactionResolveDialogComponent {
  private fb = inject(FormBuilder);
  private mitService = inject(MitService);
  private toastr = inject(ToastrService);

  transaction = input<MitTransactionDTO | null>(null);
  visible = input(false);
  closed = output<void>();
  resolved = output<MitTransactionDTO>();

  saving = signal(false);
  error = signal<string | null>(null);

  voidMode = computed(() => {
    const status = this.transaction()?.status;
    return status === 'VOID_PENDING' || status === 'VOID_FAILED';
  });
  verification = computed(() => this.transaction()?.type === 'CARD_VERIFICATION');
  outcomeOptions = computed<{ label: string; value: Outcome }[]>(() => this.voidMode()
    ? [{ label: 'Cancelada fuera de Pento', value: 'VOIDED' }]
    : [{ label: 'Aprobada', value: 'APPROVED' }, { label: 'Declinada', value: 'DECLINED' }]);

  form = this.fb.group({
    outcome: this.fb.control<Outcome | null>(null, Validators.required),
    providerReference: ['', Validators.maxLength(100)],
    providerTrackCode: ['', Validators.maxLength(100)],
    note: ['', Validators.maxLength(500)]
  });

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.form.reset({ outcome: this.voidMode() ? 'VOIDED' : null, providerReference: '', providerTrackCode: '', note: '' });
        this.error.set(null);
      }
    });
  }

  close(): void {
    if (!this.saving()) this.closed.emit();
  }

  save(): void {
    const tx = this.transaction();
    const raw = this.form.getRawValue();
    this.form.markAllAsTouched();
    if (!tx || this.form.invalid || !raw.outcome || this.saving()) return;
    const providerReference = raw.providerReference?.trim();
    const providerTrackCode = raw.providerTrackCode?.trim();
    if (raw.outcome === 'APPROVED' && !providerReference) {
      this.error.set('Para marcarla como aprobada indica la referencia del proveedor.');
      return;
    }
    if (raw.outcome === 'APPROVED' && this.verification() && !providerTrackCode) {
      this.error.set('Para aprobar un cargo de validación indica su track code; se usa para cancelarlo.');
      return;
    }
    if (raw.outcome === 'VOIDED' && !raw.note?.trim()) {
      this.error.set('Indica en la nota cómo se canceló o devolvió el cargo.');
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    this.mitService.resolveTransaction(tx.id, {
      outcome: raw.outcome,
      providerReference: providerReference || undefined,
      providerTrackCode: providerTrackCode || undefined,
      note: raw.note?.trim() || undefined
    }).subscribe({
      next: (response) => {
        this.saving.set(false);
        this.toastr.success('Transacción resuelta');
        if (response.body) this.resolved.emit(response.body);
        this.closed.emit();
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(mitApiError(err, 'No se pudo resolver la transacción').message);
      }
    });
  }
}
